/**
 * supabaseClient.ts — Supabase client singleton for the nomad frontend.
 *
 * Uses VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from the environment.
 * The anon key is safe to expose in the browser — RLS enforces all access.
 * The service-role key lives exclusively in the api-server and is never
 * accessible from the frontend.
 *
 * TOKEN GETTER — registered at module-load time (not inside a React effect)
 * so that every customFetch call carries a Bearer token from the very first
 * react-query request, even those that fire before SupabaseAuthContext mounts.
 *
 * TWO FETCH PIPELINES are both covered:
 *
 *   1. @workspace/api-client-react customFetch (generated client):
 *      setAuthTokenGetter() injects the Bearer token into the custom fetch wrapper.
 *
 *   2. Raw window.fetch() calls (use-opportunities, use-dashboard, etc.):
 *      setBearerTokenGetter() registers the same getter with the global fetch
 *      patch installed by main.tsx → bearerFetchPatch.ts.
 *
 * WHY THIS MATTERS — the COOKIE_MISSING 401 bug:
 *   Raw fetch hooks called supabase.auth.getSession() inside the fetch
 *   interceptor. In production, getSession() can throw or return null when:
 *     - An access-token refresh is already in progress (concurrent getSession
 *       calls race against the in-flight Supabase network request)
 *     - The Supabase SDK throws from inside a Replit-proxied iframe context
 *       (storage partitioning or PKCE code-verifier edge cases)
 *   bearerFetchPatch.ts silently caught those exceptions and sent the request
 *   without an Authorization header, producing COOKIE_MISSING 401 for every
 *   route except /api/auth/me (which went through customFetch, not the patch).
 *
 * THE FIX — cache the access token from onAuthStateChange:
 *   onAuthStateChange fires synchronously with the session object directly —
 *   no localStorage read, no network call, no risk of null from a race.
 *   getBearerToken() returns the cached value first; getSession() is only the
 *   fallback on cold page load (before the first auth event has fired).
 */

import { createClient } from '@supabase/supabase-js';
import { setAuthTokenGetter } from '@workspace/api-client-react';
import { setBearerTokenGetter } from './bearerFetchPatch';

const supabaseUrl  = import.meta.env.VITE_SUPABASE_URL  as string;
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnon) {
  console.error(
    '[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is not set. ' +
    'Auth will not work. Check your .replit [userenv.shared] block.',
  );
}

export const supabase = createClient(supabaseUrl ?? '', supabaseAnon ?? '', {
  auth: {
    // Persist sessions in localStorage so they survive page refreshes.
    persistSession: true,
    // Automatically refresh the access token before it expires.
    autoRefreshToken: true,
    // Detect OAuth callback hash on page load.
    detectSessionInUrl: true,
    // Use PKCE flow for better security.
    flowType: 'pkce',
  },
});

// ── Cached access token ────────────────────────────────────────────────────────
//
// Updated synchronously whenever onAuthStateChange fires.  This is the
// single source of truth for getBearerToken(); it avoids calling
// supabase.auth.getSession() from inside the fetch interceptor — a call that
// can throw or return null under concurrent token-refresh races or in
// Replit's proxied-iframe environment (PKCE storage-partitioning edge cases).
//
// Lifecycle:
//   null  — no session known yet (initial state, or after sign-out)
//   str   — valid access token from the most recent SIGNED_IN / TOKEN_REFRESHED
//
// The subscription is intentionally never unsubscribed: it must stay live for
// the entire page lifetime to keep the cache current.

let _cachedAccessToken: string | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  _cachedAccessToken = session?.access_token ?? null;
  console.log(
    `[supabase] onAuthStateChange: event=${_event} ` +
    `hasToken=${_cachedAccessToken !== null}`,
  );
});

// ── Token getter used by BOTH pipelines ───────────────────────────────────────
//
// Fast path: return the cached token set by onAuthStateChange.  This is
// always synchronously up-to-date — no async session read required.
//
// Slow path (cold page load): onAuthStateChange may not have fired yet if the
// Supabase SDK is still restoring the session from localStorage.  Fall back
// to getSession() once to seed the cache, then return.
const getBearerToken = async (): Promise<string | null> => {
  // Fast path — cache is populated.
  if (_cachedAccessToken !== null) return _cachedAccessToken;

  // Slow path — first page load, before the first auth event.
  // getSession() reads from localStorage; safe to call once here.
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) {
      // Seed the cache so subsequent calls take the fast path.
      _cachedAccessToken = data.session.access_token;
      return _cachedAccessToken;
    }
  } catch (err) {
    console.warn('[supabase] getBearerToken: getSession() threw during cold load:', err);
  }

  return null;
};

// Pipeline 1: @workspace/api-client-react customFetch (generated API client)
setAuthTokenGetter(getBearerToken);

// Pipeline 2: raw window.fetch() calls (query hooks that bypass the generated client)
setBearerTokenGetter(getBearerToken);

// ── Diagnostic exports (used by /dev/session-probe page) ──────────────────────
export { getBearerToken };
export const getCachedAccessToken = () => _cachedAccessToken;
